import React, { useState, useEffect, useRef } from 'react';
import { ReportFormData, ReportOptions, ReportSenderData, ReportSubmission } from '../types/survey';
import { getReportOptions, submitReport } from '../services/surveyService';
import './ReportModal.css';
import { useAuth } from '../contexts/AuthContext';

interface ReportModalProps {
	isOpen: boolean;
	onClose: () => void;
	surveyId: string;
	riskScore: number;
	onReportSubmitted: (success: boolean, message: string) => void;
}

export const ReportModal: React.FC<ReportModalProps> = ({ isOpen, onClose, surveyId, riskScore, onReportSubmitted }) => {
	const { token } = useAuth();
	const [formData, setFormData] = useState<ReportFormData>({
		name: '',
		surname: '',
		dateOfBirth: '',
		address: '',
		country: '',
		contact: '',
		gender: '',
		maritalStatus: '',
		stayingWithType: '',
		placeWhereLives: '',
		locationAddress: '',
		specialStatus: '',
		healthStatus: '',
		exploitationType: '',
		exploitationPhase: '',
		explanation: '',
		additionalNotes: '',
		neededSupport: '',
		neededSupportDetails: '',
		givenSupport: '',
		givenSupportDetails: '',
	});

	const [options, setOptions] = useState<ReportOptions>({
		countries: [],
		socialStatuses: [],
		healthStatuses: [],
		exploitationTypes: [],
		exploitationPhases: [],
		maritalStatus: [],
		stayingWithTypes: [],
		placesWhereLives: [],
		specialStatus: [],
		genders: [],
		exploitationPlaces: [],
		supportTypes: [],
	});
	const datePickerRef = useRef<HTMLInputElement | null>(null);
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState<{ [key: string]: string }>({});

	useEffect(() => {
		if (isOpen) {
			loadOptions();
		}
	}, [isOpen]);

	const loadOptions = async () => {
		setLoading(true);
		try {
			const reportOptions = await getReportOptions();
			setOptions(reportOptions);
		} catch (error) {
			console.error('Greška pri učitavanju opcija:', error);
		} finally {
			setLoading(false);
		}
	};

	const validateForm = (): boolean => {
		const newErrors: { [key: string]: string } = {};

		if (!formData.name.trim()) {
			newErrors.name = 'Ime je obavezno';
		}
		if (!formData.surname.trim()) {
			newErrors.surname = 'Prezime je obavezno';
		}
		if (!formData.dateOfBirth) {
			newErrors.dateOfBirth = 'Datum rođenja je obavezan';
		}
		if (!formData.gender) {
			newErrors.gender = 'Rod je obavezan';
		}
		if (!formData.address.trim()) {
			newErrors.address = 'Adresa je obavezna';
		}
		if (!formData.country) {
			newErrors.country = 'Država je obavezna';
		}
		if (!formData.contact.trim()) {
			newErrors.contact = 'Kontakt je obavezan';
		}
		if (!formData.maritalStatus) {
			newErrors.maritalStatus = 'Bračni status je obavezan';
		}
		if (!formData.stayingWithType) {
			newErrors.stayingWithType = 'Informacija o boravku je obavezna';
		}
		if (!formData.placeWhereLives) {
			newErrors.placeWhereLives = 'Lokacija je obavezna';
		}
		if (!formData.specialStatus) {
			newErrors.specialStatus = 'Specijalni status je obavezan';
		}
		if (!formData.healthStatus) {
			newErrors.healthStatus = 'Zdravstveni status je obavezan';
		}
		if (!formData.exploitationType) {
			newErrors.exploitationType = 'Oblik eksploatacije je obavezan';
		}
		if (!formData.exploitationPhase) {
			newErrors.exploitationPhase = 'Faza eksploatacije je obavezna';
		}
		if (!formData.explanation.trim()) {
			newErrors.explanation = 'Obrazloženje je obavezno';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleInputChange = (field: keyof ReportFormData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		// Ukloni grešku za polje kada korisnik počne da kuca
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: '' }));
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		if (!token) return;

		setSubmitting(true);
		try {
			const reportSubmission: ReportSubmission = {
				surveyId,
				riskScore,
				formData,
				timestamp: new Date().toISOString(),
			};

			await submitReport(reportSubmission, token);
			onReportSubmitted(true, 'Prijava je uspešno poslata.');
			onClose();

			// Resetuj formu
			setFormData({
				name: '',
				surname: '',
				dateOfBirth: '',
				address: '',
				country: '',
				contact: '',
				gender: '',
				maritalStatus: '',
				stayingWithType: '',
				placeWhereLives: '',
				locationAddress: '',
				specialStatus: '',
				healthStatus: '',
				exploitationType: '',
				exploitationPhase: '',
				explanation: '',
				additionalNotes: '',
				neededSupport: '',
				neededSupportDetails: '',
				givenSupport: '',
				givenSupportDetails: '',
			});
		} catch (error) {
			console.error('Greška pri slanju prijave:', error);
			onReportSubmitted(false, 'Greška pri slanju prijave. Molimo pokušajte ponovo.');
		} finally {
			setSubmitting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className='report-modal-overlay'>
			<div className='report-modal'>
				<div className='report-modal-header'>
					<h2>Kreiranje prijave</h2>
					<p className='report-modal-subtitle'>Informacije o potencijalnoj žrtvi trgovine ljudima</p>
					<button className='report-modal-close' onClick={onClose} disabled={submitting}>
						×
					</button>
				</div>

				<div className='report-modal-content'>
					{loading ? (
						<div className='loading-state'>
							<p>Učitavam opcije...</p>
						</div>
					) : (
						<form onSubmit={handleSubmit}>
							<div className='report-form'>
								<div className='form-section'>
									<h3>Osnovni podaci</h3>

									<div className='form-row'>
										<div className='form-group'>
											<label htmlFor='name'>Ime *</label>
											<input type='text' id='firstName' value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} className={errors.name ? 'error' : ''} placeholder='Unesite ime' />
											{errors.name && <span className='error-message'>{errors.name}</span>}
										</div>

										<div className='form-group'>
											<label htmlFor='surname'>Prezime *</label>
											<input type='text' id='lastName' value={formData.surname} onChange={(e) => handleInputChange('surname', e.target.value)} className={errors.surname ? 'error' : ''} placeholder='Unesite prezime' />
											{errors.surname && <span className='error-message'>{errors.surname}</span>}
										</div>
									</div>

									<div className='form-row  mt-3'>
										<div className='form-group'>
											<label htmlFor='dateOfBirth'>Datum rođenja *</label>
											<div
												className={`date-input-wrapper ${!formData?.dateOfBirth || '' ? 'is-empty' : ''} ${errors.dateOfBirth ? 'is-error' : ''}`}
												onMouseDown={(e) => {
													e.preventDefault();
													datePickerRef.current?.showPicker();
												}}
											>
												<input type='date' ref={datePickerRef} id='dateOfBirth' name='bday' autoComplete='bday' required value={formData?.dateOfBirth || ''} onChange={(e) => handleInputChange('dateOfBirth', e.target.value)} className='date-input' />
												{!formData?.dateOfBirth && <span className='custom-placeholder'>Izaberite datum rođenja</span>}
											</div>
											{errors.dateOfBirth && <span className='error-message'>{errors.dateOfBirth}</span>}
										</div>

										<div className='form-group'>
											<label htmlFor='gender'>Rod *</label>
											<select id='gender' value={formData.gender} onChange={(e) => handleInputChange('gender', e.target.value)} className={errors.gender ? 'error' : ''} required>
												<option value=''>Odaberite rod</option>
												{options.genders.map((gender) => (
													<option key={gender} value={gender}>
														{gender}
													</option>
												))}
											</select>
											{errors.gender && <span className='error-message'>{errors.gender}</span>}
										</div>
									</div>

									<div className='form-group  mt-3'>
										<label htmlFor='address'>Adresa *</label>
										<input type='text' id='address' value={formData.address} onChange={(e) => handleInputChange('address', e.target.value)} className={errors.address ? 'error' : ''} placeholder='Unesite adresu' />
										{errors.address && <span className='error-message'>{errors.address}</span>}
									</div>
									<div className='form-row  mt-3'>
										<div className='form-group'>
											<label htmlFor='country'>Država *</label>
											<select id='country' value={formData.country} onChange={(e) => handleInputChange('country', e.target.value)} className={errors.country ? 'error' : ''} required>
												<option value=''>Odaberite državu</option>
												{options.countries.map((country) => (
													<option key={country} value={country}>
														{country}
													</option>
												))}
											</select>
											{errors.country && <span className='error-message'>{errors.country}</span>}
										</div>

										<div className='form-group '>
											<label htmlFor='contact'>Kontakt *</label>
											<input type='text' id='contact' value={formData.contact} onChange={(e) => handleInputChange('contact', e.target.value)} className={errors.contact ? 'error' : ''} placeholder='Unesite kontakt (telefon, email, itd.)' />
											{errors.contact && <span className='error-message'>{errors.contact}</span>}
										</div>
									</div>
								</div>

								<div className='form-section'>
									<h3>Status informacije</h3>

									<div className='form-row'>
										<div className='form-group'>
											<label htmlFor='maritalStatus'>Bračni status *</label>
											<select id='maritalStatus' value={formData.maritalStatus} onChange={(e) => handleInputChange('maritalStatus', e.target.value)} className={errors.maritalStatus ? 'error' : ''} required>
												<option value=''>Odaberite bracni status</option>
												{options.maritalStatus.map((status) => (
													<option key={status} value={status}>
														{status}
													</option>
												))}
											</select>
											{errors.maritalStatus && <span className='error-message'>{errors.maritalStatus}</span>}
										</div>{' '}
										<div className='form-group'>
											<label htmlFor='stayingWithType'>Sa kime boravi: *</label>
											<select id='stayingWithType' value={formData.stayingWithType} onChange={(e) => handleInputChange('stayingWithType', e.target.value)} className={errors.stayingWithType ? 'error' : ''} required>
												<option value=''>Odaberite sa kime boravi</option>
												{options.stayingWithTypes.map((status) => (
													<option key={status} value={status}>
														{status}
													</option>
												))}
											</select>
											{errors.stayingWithType && <span className='error-message'>{errors.stayingWithType}</span>}
										</div>
									</div>
									<div className='form-row mt-3' style={{ alignItems: 'end' }}>
										<div className='form-group'>
											<label htmlFor='placesWhereLives'>Lokacija gde se trenutno nalazi pretpostavljena žrtva *</label>
											<select id='placesWhereLives' value={formData.placeWhereLives} onChange={(e) => handleInputChange('placeWhereLives', e.target.value)} className={errors.placeWhereLives ? 'error' : ''} required>
												<option value=''>Odaberite lokaciju</option>
												{options.placesWhereLives.map((status) => (
													<option key={status} value={status}>
														{status}
													</option>
												))}
											</select>
											{errors.placeWhereLives && <span className='error-message'>{errors.placeWhereLives}</span>}
										</div>

										<div className='form-group'>
											<label htmlFor='healthStatus'>Adresa lokacije *</label>
											<input type='text' id='address' value={formData.locationAddress} onChange={(e) => handleInputChange('locationAddress', e.target.value)} className={errors.locationAddress ? 'error' : ''} placeholder='Unesite adresu' disabled={formData.placeWhereLives !== 'Drugo'} />
											{errors.locationAddress && <span className='error-message'>{errors.locationAddress}</span>}
										</div>
									</div>
									<div className='form-row mt-3'>
										<div className='form-group'>
											<label htmlFor='specialStatus'>Poseban status *</label>
											<select id='specialStatus' value={formData.specialStatus} onChange={(e) => handleInputChange('specialStatus', e.target.value)} className={errors.specialStatus ? 'error' : ''} required>
												<option value=''>Odaberite posaban status</option>
												{options.specialStatus.map((status) => (
													<option key={status} value={status}>
														{status}
													</option>
												))}
											</select>
											{errors.specialStatus && <span className='error-message'>{errors.specialStatus}</span>}
										</div>

										<div className='form-group'>
											<label htmlFor='healthStatus'>Zdravstveni status *</label>
											<select id='healthStatus' value={formData.healthStatus} onChange={(e) => handleInputChange('healthStatus', e.target.value)} className={errors.healthStatus ? 'error' : ''} required>
												<option value=''>Odaberite zdravstveni status</option>
												{options.healthStatuses.map((status) => (
													<option key={status} value={status}>
														{status}
													</option>
												))}
											</select>
											{errors.healthStatus && <span className='error-message'>{errors.healthStatus}</span>}
										</div>
									</div>

									<div className='form-row mt-3'>
										<div className='form-group'>
											<label htmlFor='exploitationType'>Oblik eksploatacije *</label>
											<select id='exploitationType' value={formData.exploitationType} onChange={(e) => handleInputChange('exploitationType', e.target.value)} className={errors.exploitationType ? 'error' : ''} required>
												<option value=''>Odaberite oblik eksploatacije</option>
												{options.exploitationTypes.map((type) => (
													<option key={type} value={type}>
														{type}
													</option>
												))}
											</select>
											{errors.exploitationType && <span className='error-message'>{errors.exploitationType}</span>}
										</div>

										<div className='form-group'>
											<label htmlFor='exploitationPhase'>Da li eksploatacija traje? *</label>
											<select id='exploitationPhase' value={formData.exploitationPhase} onChange={(e) => handleInputChange('exploitationPhase', e.target.value)} className={errors.exploitationPhase ? 'error' : ''} required>
												<option value=''>Odaberite trajanje eksploatacije</option>
												{options.exploitationPhases.map((phase) => (
													<option key={phase} value={phase}>
														{phase}
													</option>
												))}
											</select>
											{errors.exploitationPhase && <span className='error-message'>{errors.exploitationPhase}</span>}
										</div>
									</div>
									<div className='form-group mt-3'>
										<label htmlFor='surname'>Obrazloženje *</label>
										<textarea id='surname' value={formData.explanation} onChange={(e) => handleInputChange('explanation', e.target.value)} className={errors.explanation ? 'error' : ''} rows={1} placeholder='Unesite obrazloženje' />
									</div>
								</div>

								<div className='form-section'>
									<h3>Usluge i oblici podrške</h3>
									<div className='form-group'>
										<label htmlFor='exploitationPhase'>Potrebne usluge i oblici podrške *</label>
										<select id='exploitationPhase' value={formData.neededSupport} onChange={(e) => handleInputChange('neededSupport', e.target.value)} className={errors.exploitationPhase ? 'error' : ''} required>
											<option value=''>Odaberite trajanje eksploatacije</option>
											{options.supportTypes.map((phase) => (
												<option key={phase} value={phase}>
													{phase}
												</option>
											))}
										</select>
										{errors.neededSupport && <span className='error-message'>{errors.neededSupport}</span>}
									</div>

									<div className='form-group mt-1'>
										{/* <label htmlFor='additionalNotes'>Unesite opravdanje</label> */}
										<textarea id='additionalNotes' value={formData.neededSupportDetails} onChange={(e) => handleInputChange('neededSupportDetails', e.target.value)} rows={2} placeholder='Unesite slobodnu formu...' />
									</div>

									<div className='form-group mt-3'>
										<label htmlFor='exploitationPhase'>Pružene usluge i oblici podrške *</label>
										<select id='exploitationPhase' value={formData.givenSupport} onChange={(e) => handleInputChange('givenSupport', e.target.value)} className={errors.exploitationPhase ? 'error' : ''} required>
											<option value=''>Odaberite trajanje eksploatacije</option>
											{options.supportTypes.map((phase) => (
												<option key={phase} value={phase}>
													{phase}
												</option>
											))}
										</select>
										{errors.givenSupport && <span className='error-message'>{errors.givenSupport}</span>}
									</div>
									<div className='form-group mt-1'>
										{/* <label htmlFor='requiredServices'>Unesite potrebne usluge</label> */}
										<textarea id='requiredServices' value={formData.givenSupportDetails} onChange={(e) => handleInputChange('givenSupportDetails', e.target.value)} rows={2} placeholder='Unesite slobodnu formu...' />
									</div>
								</div>
							</div>
							<div className='form-actions'>
								<button type='button' className='btn btn-secondary' onClick={onClose} disabled={submitting}>
									Otkaži
								</button>
								<button type='submit' className='btn btn-primary' disabled={submitting}>
									{submitting ? 'Šalje se...' : 'Pošalji prijavu'}
								</button>
							</div>
						</form>
					)}
				</div>
			</div>
		</div>
	);
};
